<template>
  <nav class="search-navigation" aria-label="Search menu">
    <ul
      v-accessible-menubar
      class="search-navigation__list"
      aria-label="Search menu"
      role="menubar"
    >
      <li
        v-for="vertical in verticals"
        :key="vertical.id"
        class="search-navigation__item"
        role="none"
      >
        <ETab
          :url="getUrl(vertical.path)"
          :icon="vertical.id"
          :highlighted="isCurrentVertical(vertical.path)"
          :track-id="`nav_${vertical.id}`"
          :data-test-id="`search-navigation-${vertical.id}`"
          bold-highlight-text
        >
          {{ $t(`common.header.verticals.${vertical.id}`) }}
        </ETab>
      </li>
      <li class="search-navigation__item" role="none">
        <ETab
          :dropdown-title="$t('common.header.dropdowns.maps.title')"
          :highlight-bar="false"
          icon="maps"
          data-test-id="search-navigation-maps"
          data-track-id="nav_maps"
        >
          {{ this.$t('common.header.verticals.maps') }}
          <template #dropdown>
            <nav>
              <EList>
                <EListItem v-if="ecosiaMapsShown" role="none">
                  <a
                    :href="getUrl('/map')"
                    class="search-navigation__dropdown-link"
                    data-test-id="search-navigation-maps-ecosia"
                    data-track-id="maps_ecosia"
                    target="_blank"
                    rel="noopener"
                    role="menuitem"
                  >
                    <EIcon
                      class="search-navigation__dropdown-icon"
                      icon="source-ecosia"
                    />
                    Ecosia {{ $t('common.header.verticals.maps') }}
                  </a>
                </EListItem>
                <EListItem v-if="bingMapsShown" role="none">
                  <a
                    :href="bingMapsUrl"
                    class="search-navigation__dropdown-link"
                    data-test-id="search-navigation-maps-bing"
                    data-track-id="maps_bing"
                    target="_blank"
                    rel="noopener"
                    role="menuitem"
                  >
                    <EIcon
                      class="search-navigation__dropdown-icon"
                      icon="source-bing"
                      size="m"
                    />
                    Bing
                  </a>
                </EListItem>
                <EListItem v-if="treedayShown" role="none">
                  <a
                    :href="treedayUrl"
                    class="search-navigation__dropdown-link"
                    data-test-id="search-navigation-maps-treeday"
                    data-track-id="maps_treeday"
                    target="_blank"
                    rel="noopener"
                    role="menuitem"
                  >
                    <EIcon
                      class="search-navigation__dropdown-icon"
                      icon="source-treeday"
                      size="m"
                    />
                    Treeday
                  </a>
                </EListItem>
                <EListItem role="none">
                  <a
                    :href="googleMapsUrl"
                    class="search-navigation__dropdown-link"
                    data-test-id="search-navigation-maps-google"
                    data-track-id="maps_google"
                    target="_blank"
                    rel="noopener"
                    role="menuitem"
                  >
                    <EIcon
                      class="search-navigation__dropdown-icon"
                      icon="source-google-maps"
                      size="m"
                    />
                    Google Maps
                  </a>
                </EListItem>
              </EList>
            </nav>
          </template>
        </ETab>
      </li>
      <li class="search-navigation__item" role="none">
        <ETab
          :dropdown-title="$t('common.header.dropdowns.searchoptions.title')"
          :highlight-bar="false"
          icon="more"
          data-test-id="search-navigation-more"
          data-track-id="nav_more"
        >
          {{ this.$t('common.header.searchoptions.more') }}
          <template #dropdown>
            <nav>
              <EList>
                <EListItem v-for="searchTag in searchTags" :key="searchTag.title" role="none">
                  <a
                    :href="searchTag.url"
                    class="search-navigation__dropdown-link"
                    target="_blank"
                    rel="noopener"
                    role="menuitem"
                    tabindex="0"
                    data-test-id="search-tag-link"
                    :data-track-id="`more_${searchTag.title}`"
                  >
                    <EIcon
                      class="search-navigation__dropdown-icon"
                      :icon="searchTag.icon"
                      size="m"
                    />
                    {{ $t(`common.header.searchoptions.${searchTag.title}`) }}
                  </a>
                </EListItem>
                <EListItem role="none">
                  <a
                    :href="ecosiaTravelUrl"
                    target="_blank"
                    rel="noopener"
                    role="menuitem"
                    tabindex="0"
                    class="search-navigation__dropdown-link"
                    data-test-id="ecosia-travel-link"
                    data-track-id="more-ecosia-hotels-link"
                  >
                    <EIcon
                      class="search-navigation__dropdown-icon"
                      icon="source-ecosia"
                      size="m"
                    />
                    {{ $t('common.header.searchoptions.hotels') }}
                    <ELabel class="search-navigation__dropdown-label">
                      {{ $t('common.header.searchoptions.beta.tag') }}
                    </ELabel>
                  </a>
                </EListItem>
              </EList>
            </nav>
          </template>
        </ETab>
      </li>
      <div class="search-navigation__divider" />
      <li
        v-if="filtersToggle"
        class="search-navigation__item search-navigation__item--align-right"
        role="none"
      >
        <ETab
          ref="filters"
          :highlight-bar="false"
          :dropdown-title="$t('common.header.dropdowns.filters.title')"
          :highlighted="filtersShown"
          dropdown-size="l"
          dropdown-side="right"
          data-test-id="search-navigation-filters"
          data-track-id="nav_filters"
          @dropdown-visibility-change="onFiltersVisibilityChange"
        >
          {{ this.$t('common.header.dropdowns.filters.title') }}
        </ETab>
      </li>
      <li class="search-navigation__item search-navigation__item--align-right" role="none">
        <ETab
          :highlight-bar="false"
          :dropdown-title="$t('common.header.dropdowns.settings.title')"
          dropdown-size="l"
          dropdown-side="right"
          data-test-id="search-navigation-settings"
          data-track-id="nav_settings"
        >
          {{ this.$t('common.header.menu.settings') }}
          <template #dropdown>
            <ESettingsDropdown />
          </template>
        </ETab>
      </li>
    </ul>
  </nav>
</template>

<script>
import { mapState, mapMutations } from 'vuex';

import { getHashParam, setHashParam, deleteHashParam } from '~~/common/js/universal/hash-params';
import { TREEDAY_URL } from '~~/common/js/constants/urls';
import { searchTags } from '~~/common/js/universal/search-tags/dropdown';
import { addTagToQuery } from '~~/common/js/universal/search-tags/utils';
import { getBingMapsSearchUrl, getGoogleMapsSearchUrl, getHotelsUrl } from '~~/common/js/universal/url-utils';
import { getUrlQuery } from '~~/common/js/universal/search';
import getCurrencyForMarket from '~~/common/js/universal/currency';

import ELabel from '~~/common/vue/components/label';
import EIcon from '~~/common/vue/components/icon';
import EList from '~~/common/vue/components/list';
import EListItem from '~~/common/vue/components/list-item';
import ETab from '~~/common/vue/components/tab';

import { FILTERS_KEY } from '~~/search/common/js/universal/hash-params';

import ESettingsDropdown from './settings-dropdown';

const shoppingVertical = {
  id: 'shopping',
  path: '/shopping',
};
export const verticals = [{
  id: 'web',
  path: '/search',
}, {
  id: 'images',
  path: '/images',
}, {
  id: 'news',
  path: '/news',
}, {
  id: 'videos',
  path: '/videos',
}];

/**
 * Allows the user to navigate between verticals and other search options.
 * Requires the `user` Vuex module.
 */
export default {
  name: 'ESearchNavigation',
  components: {
    ELabel,
    EIcon,
    EList,
    EListItem,
    ESettingsDropdown,
    ETab,
  },
  props: {
    filtersToggle: {
      type: Boolean,
      default: true,
    },
    shoppingLink: {
      type: Boolean,
      default: false,
    }
  },
  data: () => ({
    focusedElement: null,
  }),
  computed: {
    ...mapState('search', ['filtersShown']),
    query() {
      return this.$route.query.originalQuery || this.$route.query.q || '';
    },
    verticals() {
      if (this.shoppingLink) {
        const atIndex = 2;
        return [...verticals.slice(0, atIndex), shoppingVertical, ...verticals.slice(atIndex)];
      }
      return verticals;
    },
    bingMapsUrl() {
      return getBingMapsSearchUrl(this.query);
    },
    googleMapsUrl() {
      return getGoogleMapsSearchUrl(this.query);
    },
    treedayUrl() {
      return TREEDAY_URL;
    },
    ecosiaTravelUrl() {
      const { bingMarketCode: market, language, deviceType } = this.$store.state.user;
      const currencyCode = getCurrencyForMarket(market);
      return getHotelsUrl({ language, market, deviceType, currencyCode });
    },
    searchTags() {
      const { path } = this.verticals.find(({ id }) => id === 'web');
      return searchTags.map(({ title, icon, tag }) => {
        const fromQuery = getUrlQuery(this.$route.query);
        const query = { ...fromQuery, q: addTagToQuery(this.query, tag) };
        const url = this.$router.resolve({ path, query }).href;
        return { title, icon, url };
      });
    },
    treedayShown() {
      return ['de-de', 'de-at'].includes(this.$store.state.user.bingMarketCode);
    },
    bingMapsShown() {
      return !this.treedayShown;
    },
    ecosiaMapsShown() {
      return this.$store.state.user.bingMarketCode === 'fr-fr';
    },
  },
  beforeMount() {
    // on page load, the filters should be open if the hash param is set to true
    if (getHashParam(FILTERS_KEY) === 'true') {
      this.setFiltersShown(true);
    }
  },
  mounted() {
    // the Filters button should scroll into view
    // if the filters were shown during beforeMount
    if (this.filtersShown) {
      this.$refs.filters.$el.scrollIntoView(false);
    }
  },
  methods: {
    ...mapMutations('search', ['setFiltersShown']),
    isCurrentVertical(path) {
      return path === this.$route.path;
    },
    getUrl(path) {
      const query = getUrlQuery(this.$route.query);
      return this.$router.resolve({ path, query }).href;
    },
    onFiltersVisibilityChange(isVisible) {
      this.setFiltersShown(isVisible);

      if (isVisible) {
        setHashParam(FILTERS_KEY, 'true');
      } else {
        deleteHashParam(FILTERS_KEY);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.search-navigation {
  overflow: scroll;
  overflow-y: visible;
  scrollbar-width: none;
  margin: 0 -1 * $space-s;

  @include tablet {
    overflow: visible;
  }
}

.search-navigation__list {
  list-style-type: none;
  margin: 0;
  padding: 0 $space-s;
  width: max-content;

  @include desktop {
    display: flex;
    width: $page-container-width-desktop + 2 * $space-s;
  }

  &:focus {
    outline: none;
  }
}

.search-navigation__item {
  display: inline-block;
  flex-shrink: 0;

  & + & {
    @include tablet {
      margin-left: $space-1s;
    }

    @include desktop {
      margin-left: $space-s;
    }
  }
}

.search-navigation__item.search-navigation__item--align-right {
  @include desktop {
    margin-left: auto;
    margin-right: $space-s;

    &:last-of-type {
      margin-right: $space-2s;
    }

    & + & {
      margin-left: 0;
    }
  }
}

.search-navigation__dropdown-link {
  align-items: center;
  display: flex;
  font-size: $font-m;
  height: $space-l;
  white-space: nowrap;

  @include desktop {
    font-size: $font-s;
  }

  &:focus {
    @extend %keyboard-nav-focus;
  }
}

.search-navigation__dropdown-icon {
  margin-right: $space-1s;
}

.search-navigation__dropdown-label {
  margin-left: $space-1s;
}

.search-navigation__divider {
  background: $color-border;
  border: none;
  display: inline-block;
  flex-shrink: 0;
  height: $space-m;
  margin: 0 $space-s 0;
  vertical-align: middle;
  width: 1px;

  @include tablet {
    margin: 0 $space-m 0;
  }

  @include desktop {
    display: none;
  }
}
</style>
